<template>
  <div class="insights-scope-bar">
    <div v-if="showCompanyPicker" class="insights-scope-bar__field">
      <label class="insights-scope-bar__label">Company</label>
      <q-select
        :model-value="selectedCompany"
        @update:model-value="emitCompany"
        :options="companies"
        :loading="companiesLoading"
        :disable="companiesLoading || companies.length === 0"
        option-value="companyName"
        option-label="companyName"
        placeholder="Select a company"
        outlined
        dense
        emit-value
        map-options
      />
    </div>

    <div class="insights-scope-bar__field">
      <label class="insights-scope-bar__label">Date range</label>
      <q-input
        outlined
        readonly
        dense
        :model-value="rangeLabel"
        class="cursor-pointer"
        @click="datePickerOpen = true"
      >
        <template v-slot:append>
          <q-icon
            name="event"
            class="cursor-pointer"
            @click.stop="datePickerOpen = true"
          >
            <q-popup-proxy
              v-model="datePickerOpen"
              cover
              transition-show="scale"
              transition-hide="scale"
              @show="openPicker"
            >
              <q-date
                v-model="tempRange"
                range
                mask="YYYY-MM-DD"
                :options="dateAllowed"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="OK" color="primary" flat @click="saveRange" />
                  <q-btn v-close-popup label="Cancel" color="primary" flat @click="cancelRange" />
                </div>
              </q-date>
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
      <div class="insights-scope-bar__hint">
        Data is available from April 15, 2025 onwards.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { InsightsCompany, InsightsDateRange } from '../types';

const props = defineProps<{
  showCompanyPicker: boolean;
  companies: InsightsCompany[];
  companiesLoading: boolean;
  selectedCompany: string | null;
  dateRange: InsightsDateRange;
}>();

const emit = defineEmits<{
  (e: 'update:selectedCompany', value: string | null): void;
  (e: 'update:dateRange', value: InsightsDateRange): void;
}>();

const datePickerOpen = ref(false);
const tempRange = ref<InsightsDateRange | string | null>(null);

const rangeLabel = computed(
  () => `${props.dateRange.from || '--'} to ${props.dateRange.to || '--'}`
);

const dateAllowed = (date: string) => {
  // Matches the partner-portal floor of 2025-04-15 — data older than that is not
  // populated in analytics, so picking it would yield an empty view.
  const today = new Date().toISOString().split('T')[0].replace(/-/g, '/');
  return date >= '2025/04/15' && date <= today;
};

const openPicker = () => {
  if (props.dateRange.from === props.dateRange.to) {
    tempRange.value = props.dateRange.to;
  } else {
    tempRange.value = { ...props.dateRange };
  }
};

const saveRange = () => {
  if (typeof tempRange.value === 'string' && tempRange.value) {
    emit('update:dateRange', { from: tempRange.value, to: tempRange.value });
  } else if (tempRange.value && typeof tempRange.value === 'object') {
    emit('update:dateRange', {
      from: tempRange.value.from ?? '',
      to: tempRange.value.to ?? ''
    });
  }
  datePickerOpen.value = false;
};

const cancelRange = () => {
  tempRange.value = { ...props.dateRange };
  datePickerOpen.value = false;
};

const emitCompany = (value: string | null) =>
  emit('update:selectedCompany', value);
</script>

<style scoped lang="scss">
.insights-scope-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;

  &__field {
    min-width: 220px;
    flex: 0 1 260px;
  }

  &__label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #4c4c4c;
    margin-bottom: 4px;
  }

  &__hint {
    margin-top: 4px;
    font-size: 11px;
    color: #8b8b8b;
  }
}
</style>
